import type { GatewayBrowserClient } from "../gateway.ts";

export type ModelPricingEntry = {
  provider: string;
  modelId: string;
  name: string;
  cost: {
    input: number;
    output: number;
    cacheRead: number;
    cacheWrite: number;
  };
};

export type BillingPlanEntry = {
  id: string;
  name: string;
  maintenanceFeeCents: number;
  includedTokens: number;
  overagePerMillionCents: number;
  isSubscription: boolean;
};

export type InferenceConfigState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  inferenceLoading: boolean;
  inferenceEntries: ModelPricingEntry[];
  inferenceDirty: Set<string>;
  inferenceSaving: boolean;
  inferenceError: string | null;
  configHash: string | null;
  // Billing fields
  billingMaintenanceFeeCents: number;
  billingSelectedPlanId: string | null;
  billingExtraPerToken: boolean;
  billingPlans: BillingPlanEntry[];
  billingDirty: boolean;
};

export function inferenceDirtyKey(provider: string, modelId: string): string {
  return `${provider}:${modelId}`;
}

type ConfigResult = {
  raw?: string;
  hash?: string;
};

export async function loadInferenceConfig(state: InferenceConfigState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.inferenceLoading) {
    return;
  }
  state.inferenceLoading = true;
  state.inferenceError = null;
  try {
    const snap = await state.client.request<ConfigResult>("config.get", {});
    state.configHash = snap.hash ?? null;
    const config = JSON.parse(snap.raw ?? "{}");
    const providers = config?.models?.providers ?? {};
    const entries: ModelPricingEntry[] = [];
    for (const [providerId, providerConfig] of Object.entries(providers) as Array<
      [string, Record<string, unknown>]
    >) {
      const models = (providerConfig.models as Array<Record<string, unknown>>) ?? [];
      for (const model of models) {
        const cost = (model.cost as ModelPricingEntry["cost"]) ?? {
          input: 0,
          output: 0,
          cacheRead: 0,
          cacheWrite: 0,
        };
        entries.push({
          provider: providerId,
          modelId: String(model.id ?? ""),
          name: String(model.name ?? model.id ?? ""),
          cost: {
            input: Number(cost.input) || 0,
            output: Number(cost.output) || 0,
            cacheRead: Number(cost.cacheRead) || 0,
            cacheWrite: Number(cost.cacheWrite) || 0,
          },
        });
      }
    }
    state.inferenceEntries = entries;
    state.inferenceDirty = new Set();

    // Load billing config from the same config snapshot
    const billingConfig = config?.billing ?? {};
    state.billingMaintenanceFeeCents = Number(billingConfig.maintenanceFeeCents) || 1500;
    state.billingSelectedPlanId = billingConfig.planId ?? null;
    state.billingExtraPerToken = billingConfig.extraPerToken === true;
    state.billingDirty = false;
  } catch (err) {
    state.inferenceError = String(err);
  } finally {
    state.inferenceLoading = false;
  }

  // Load billing plans (separate RPC, non-blocking)
  try {
    const plansRes = await state.client.request<{ ok: boolean; plans?: BillingPlanEntry[] }>(
      "igloobot.billing.plans.list",
      {},
    );
    if (plansRes.ok && plansRes.plans) {
      state.billingPlans = plansRes.plans;
    }
  } catch {
    // Non-critical — billing plans may not be available yet
  }
}

export function updateModelPricing(
  state: InferenceConfigState,
  provider: string,
  modelId: string,
  field: keyof ModelPricingEntry["cost"],
  value: number,
) {
  const entry = state.inferenceEntries.find(
    (e) => e.provider === provider && e.modelId === modelId,
  );
  if (!entry) {
    return;
  }
  entry.cost[field] = value;
  state.inferenceDirty.add(inferenceDirtyKey(provider, modelId));
}

export function updateMaintenanceFee(state: InferenceConfigState, cents: number) {
  state.billingMaintenanceFeeCents = cents;
  state.billingDirty = true;
}

export function updateSelectedPlan(state: InferenceConfigState, planId: string) {
  state.billingSelectedPlanId = planId || null;
  state.billingDirty = true;
}

export function updateExtraPerToken(state: InferenceConfigState, enabled: boolean) {
  state.billingExtraPerToken = enabled;
  state.billingDirty = true;
}

export async function saveBillingConfig(state: InferenceConfigState) {
  if (!state.client || !state.connected || state.inferenceSaving) {
    return;
  }
  if (!state.billingDirty) {
    return;
  }
  state.inferenceSaving = true;
  state.inferenceError = null;
  try {
    if (!state.configHash) {
      const snap = await state.client.request<ConfigResult>("config.get", {});
      state.configHash = snap.hash ?? null;
    }
    if (!state.configHash) {
      state.inferenceError = "Could not read config hash — try refreshing";
      return;
    }
    const patch: Record<string, unknown> = {
      billing: {
        maintenanceFeeCents: state.billingMaintenanceFeeCents,
        planId: state.billingSelectedPlanId,
        extraPerToken: state.billingExtraPerToken,
      },
    };
    const res = await state.client.request<PatchResult>("config.patch", {
      raw: JSON.stringify(patch),
      baseHash: state.configHash,
    });
    if (res.ok) {
      state.billingDirty = false;
      state.configHash = null;
    } else {
      state.inferenceError = res.error ?? "Failed to save billing config";
    }
  } catch (err) {
    state.inferenceError = String(err);
  } finally {
    state.inferenceSaving = false;
  }
}

type PatchResult = { ok: boolean; error?: string };

export async function saveInferenceConfig(state: InferenceConfigState) {
  if (!state.client || !state.connected || state.inferenceSaving) {
    return;
  }
  if (state.inferenceDirty.size === 0) {
    return;
  }
  state.inferenceSaving = true;
  state.inferenceError = null;
  try {
    // Fetch the full config so we can preserve all model fields (contextWindow,
    // maxTokens, etc.) — applyMergePatch replaces arrays, not merges them.
    const snap = await state.client.request<ConfigResult>("config.get", {});
    state.configHash = snap.hash ?? null;
    if (!state.configHash) {
      state.inferenceError = "Could not read config hash — try refreshing";
      return;
    }
    const fullConfig = JSON.parse(snap.raw ?? "{}");
    const providers = fullConfig?.models?.providers ?? {};

    const dirtyKeys = Array.from(state.inferenceDirty);
    const dirtyProviders = new Set(dirtyKeys.map((k) => k.split(":")[0]));

    // Build a patch with the FULL models array for each dirty provider,
    // only updating the cost field on dirty models
    const patch: Record<string, unknown> = { models: { providers: {} } };
    const providersMap = (patch.models as Record<string, unknown>).providers as Record<string, unknown>;

    for (const providerId of dirtyProviders) {
      const providerConfig = providers[providerId];
      if (!providerConfig) continue;

      // Deep clone the provider config to preserve all fields
      const providerClone = JSON.parse(JSON.stringify(providerConfig));
      const models = (providerClone.models as Array<Record<string, unknown>>) ?? [];

      for (const key of dirtyKeys) {
        const [prov, modelId] = key.split(":");
        if (prov !== providerId) continue;
        const entry = state.inferenceEntries.find(
          (e) => e.provider === providerId && e.modelId === modelId,
        );
        if (!entry) continue;

        const model = models.find((m) => m.id === modelId);
        if (model) {
          model.cost = { ...entry.cost };
        }
      }

      providersMap[providerId] = providerClone;
    }

    const res = await state.client.request<PatchResult>("config.patch", {
      raw: JSON.stringify(patch),
      baseHash: state.configHash,
    });

    if (res.ok) {
      state.inferenceDirty = new Set();
      state.configHash = null;
    } else {
      state.inferenceError = res.error ?? "Failed to save pricing";
    }
  } catch (err) {
    state.inferenceError = String(err);
  } finally {
    state.inferenceSaving = false;
  }
}
